import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchAnalytics } from "@admin/services/adminApi";
import { PageHeader, StatCard } from "@admin/components/Primitives";

const fmt = (n) => (n || 0).toLocaleString("en-IN");

export default function AdminAnalytics() {
  const [s, setS] = useState(null);

  useEffect(() => {
    fetchAnalytics().then(setS).catch((e) => toast.error(e?.response?.data?.detail || "Could not load analytics"));
  }, []);

  if (!s) return <div className="text-slate-500" data-testid="analytics-loading">Loading analytics…</div>;

  return (
    <>
      <PageHeader testid="admin-analytics-header" title="Analytics" subtitle="Revenue, consultations, conversion, and lawyer performance." />

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Revenue</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard testid="rev-total" label="Total Revenue" value={`₹${fmt(s.revenue)}`} hint="From paid consultations" accent="navy" />
        <StatCard testid="rev-paid-consults" label="Paid Consultations" value={fmt(s.paid_consultations)} hint={`of ${fmt(s.consultations)} total`} accent="gold" />
        <StatCard testid="rev-conversion" label="Chat → Pay Conversion" value={`${s.conversion_rate}%`} hint={`${s.paid_user_count}/${s.chat_user_count} users`} accent="emerald" />
        <StatCard testid="rev-refunded" label="Refunded" value={fmt(s.refunded_consultations)} hint="Lifetime" accent="rose" />
      </div>

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Pipeline</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard testid="pipe-active" label="Active" value={fmt(s.active_consultations)} hint="confirmed/accepted/live" accent="navy" />
        <StatCard testid="pipe-pending" label="Pending" value={fmt(s.pending_consultations)} hint="awaiting status" accent="gold" />
        <StatCard testid="pipe-completed" label="Completed" value={fmt(s.completed_consultations)} hint="closed successfully" accent="emerald" />
        <StatCard testid="pipe-cancelled" label="Cancelled" value={fmt(s.cancelled_consultations)} hint="cancelled / rejected" accent="rose" />
      </div>

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Marketplace</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard testid="mkt-users" label="Total Users" value={fmt(s.users)} hint="Lifetime sign-ups" accent="navy" />
        <StatCard testid="mkt-lawyers" label="Lawyers" value={`${fmt(s.active_lawyers)} / ${fmt(s.lawyers)}`} hint={`${s.verified_lawyers} verified`} accent="gold" />
        <StatCard testid="mkt-articles" label="Articles" value={fmt(s.articles)} hint="Knowledge hub" accent="emerald" />
        <StatCard testid="mkt-chats" label="AI Chats" value={fmt(s.chats)} hint={`${fmt(s.queries)} queries asked`} accent="rose" />
      </div>

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Onboarding & KYC</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard testid="kyc-pending" label="KYC Pending Review" value={fmt(s.kyc?.pending_reviews)} hint="Awaiting reviewer" accent="gold" />
        <StatCard testid="kyc-approved-24h" label="KYC Approved (24h)" value={fmt(s.kyc?.approved_24h)} hint={`${fmt(s.kyc?.approved_total)} lifetime`} accent="emerald" />
        <StatCard testid="kyc-rejected-24h" label="KYC Rejected (24h)" value={fmt(s.kyc?.rejected_24h)} hint={`${fmt(s.kyc?.rejected_total)} lifetime`} accent="rose" />
        <StatCard testid="kyc-avg-review" label="Avg Review Time" value={
          s.kyc?.avg_review_seconds == null ? "—" :
            s.kyc.avg_review_seconds < 60 ? `${s.kyc.avg_review_seconds}s` :
            s.kyc.avg_review_seconds < 3600 ? `${Math.round(s.kyc.avg_review_seconds / 60)}m` :
            `${(s.kyc.avg_review_seconds / 3600).toFixed(1)}h`
        } hint="Last 200 decisions" accent="navy" />
        <StatCard testid="apps-pending" label="Applications Pending" value={fmt(s.applications?.pending)} hint="Self-serve queue" accent="gold" />
        <StatCard testid="apps-approved" label="Applications Approved" value={fmt(s.applications?.approved)} hint="Lifetime" accent="emerald" />
        <StatCard testid="apps-rejected" label="Applications Rejected" value={fmt(s.applications?.rejected)} hint="Lifetime" accent="rose" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 text-sm text-slate-500 flex items-start gap-3" data-testid="analytics-export-note">
        <p><strong>CSV export</strong> coming in the next phase. Need a metric breakout? Ask your dev team to add it to <code>/admin/analytics</code>.</p>
      </div>
    </>
  );
}
